// ─── Buy-box rules service ──────────────────────────────────────────────────
// Reads the current buy-box rule and the pointer to it (app_settings.
// buy_box_current_version), and lets callers publish a new version.
//
// Cached in-process for 60s so the write-hook path in supabase.js doesn't
// hit the DB on every insertDeal / updateProperty call. Save flushes the
// cache immediately so the newly-published rule takes effect right away.

import { supabase } from "../supabase";

const EMPTY_RULE = {
  msas: [],
  product_types: [],
  sf_min: null,
  sf_max: null,
  cap_rate_min: null,
};

let _cache = null;  // { rule, version, expiresAt }
const TTL_MS = 60_000;

function _isFresh() {
  return _cache && Date.now() < _cache.expiresAt;
}

// Force-refresh next read. Call after publishing a new rule so the write
// hooks pick it up on their very next call.
export function invalidateBuyBoxCache() {
  _cache = null;
}

// Returns { rule, version }. rule is always an object with all keys
// present (falling back to EMPTY_RULE shape); version is an integer.
export async function getActiveBuyBox() {
  if (_isFresh()) return { rule: _cache.rule, version: _cache.version };

  const [settingRes, versionsRes] = await Promise.all([
    supabase.from("app_settings").select("value").eq("key", "buy_box_current_version").maybeSingle(),
    supabase.from("buy_box_versions").select("version, rules").order("version", { ascending: false }).limit(1),
  ]);

  const versionStr = settingRes?.data?.value;
  let target = versionStr ? parseInt(versionStr, 10) : null;
  if (!Number.isFinite(target) || target <= 0) {
    // No pointer set — fall back to the newest row if there is one.
    target = versionsRes?.data?.[0]?.version ?? null;
  }

  let ruleRow = null;
  if (target != null) {
    const { data } = await supabase
      .from("buy_box_versions")
      .select("version, rules")
      .eq("version", target)
      .maybeSingle();
    ruleRow = data;
  }

  const rule = { ...EMPTY_RULE, ...(ruleRow?.rules || {}) };
  const version = ruleRow?.version ?? 0;

  _cache = { rule, version, expiresAt: Date.now() + TTL_MS };
  return { rule, version };
}

// Full history — for the Settings panel timeline. Ordered newest-first.
export async function listBuyBoxVersions() {
  const { data, error } = await supabase
    .from("buy_box_versions")
    .select("version, rules, changed_by, changed_at, notes")
    .order("version", { ascending: false });
  if (error) { console.error("listBuyBoxVersions:", error.message); return []; }
  return data || [];
}

// Publish a new rule. Assigns the next version number, inserts, and
// updates the app_settings pointer so it becomes active immediately.
// changedBy is a free-text string (typically the user's email); notes
// is an optional human summary.
export async function publishBuyBoxRule(rule, { changedBy = null, notes = null } = {}) {
  // Next version = max(version) + 1. Simple + serial; races are fine here
  // because publishing is rare and app_settings.value acts as the final
  // tiebreaker anyway.
  const { data: maxRow } = await supabase
    .from("buy_box_versions")
    .select("version")
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextVersion = (maxRow?.version ?? 0) + 1;

  const normalized = { ...EMPTY_RULE, ...(rule || {}) };
  const { error: insErr } = await supabase.from("buy_box_versions").insert({
    version: nextVersion,
    rules: normalized,
    changed_by: changedBy,
    notes: notes,
  });
  if (insErr) return { error: insErr.message };

  const { error: upErr } = await supabase
    .from("app_settings")
    .update({ value: String(nextVersion) })
    .eq("key", "buy_box_current_version");
  if (upErr) return { error: upErr.message };

  invalidateBuyBoxCache();
  return { version: nextVersion, rule: normalized };
}
